import { Injectable } from '@angular/core';
import { GoogleGenAI, Type } from '@google/genai';

export enum GeminiSchemaType {
  Profiles = 'profiles',
  Companies = 'companies',
  Prompts = 'prompts',
  CSVCompanies = 'csv-companies',
  CSVProducts = 'csv-products',
}

@Injectable({
  providedIn: 'root'
})
export class GeminiService {
  private ai: GoogleGenAI;
  private readonly modelName = 'gemini-flash-latest';

  private companiesResponseSchema = {
    type: Type.OBJECT,
    required: ['source'],
    properties: {
      source: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          required: ['Company Name', 'Industry', 'Country/Region', 'How Product Is Used', 'Source', 'Probability'],
          properties: {
            'Company Name': { type: Type.STRING },
            Industry: { type: Type.STRING },
            'Country/Region': { type: Type.STRING },
            'How Product Is Used': { type: Type.STRING },
            Scope: { type: Type.STRING },
            Source: { type: Type.STRING },
            Probability: { type: Type.STRING },
            dateFirstVerified: { type: Type.STRING },
            dateLastVerified: { type: Type.STRING }
          }
        }
      }
    }
  };

  private profilesResponseSchema = {
    type: Type.OBJECT,
    required: ['source'],
    properties: {
      source: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          required: ['Name', 'Title','Source'],
          properties: {
            'Name': { type: Type.STRING },
            'Title': { type: Type.STRING },
            'Department': { type: Type.STRING },
            'Level': { type: Type.STRING },
            'Location': { type: Type.STRING },
            'Core Skills': { type: Type.STRING },
            'Source': { type: Type.STRING },
          }
        }
      }
    }
  };

  private promptsResponseSchema = {
    type: Type.OBJECT,
    properties: {
      prompts: {
        type: Type.ARRAY,
        items: {
          type: Type.STRING
        }
      }
    },
    required: ["prompts"]
  }

  private domainNameCompaniesUsingProductResponseSchema = {
    type: 'OBJECT',
    properties: {
      items: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            ProductName: {
              type: 'STRING',
            },
            ProductVendorDomainName: {
              type: 'STRING',
            },
            CompanyDomainName: {
              type: 'STRING',
            },
            Country: {
              type: 'STRING',
            },
            Industry: {
              type: 'STRING',
            },
            Probability: {
              type: 'STRING',
            },
            Source: {
              type: 'STRING',
            },
            dateFirstVerified: {
              type: 'STRING',
            },
             dateLastVerified: {
              type: 'STRING',
            },
          },
          propertyOrdering: ['ProductName','ProductVendorDomainName', 'CompanyDomainName','Country','Industry','Probability','Source','dateFirstVerified','dateLastVerified'],
          required: ['ProductName','ProductVendorDomainName', 'CompanyDomainName','Country','Industry','Probability','Source','dateFirstVerified','dateLastVerified'],
        },
      },
    },
    propertyOrdering: ['items'],
    required: ['items'],
  };

   private csvProductsResponseSchema = {
    properties: {
      products: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          required: [
            'Company',
            'CompanyDomain',
            'Country',
            'Vendor',
            'VendorDomain',
            'Product',
            'Source',
            'HitCount',
            'DateFirstVerified',
            'DateLastVerified',
          ],
          properties: {
            Company: {type: 'STRING',},
            CompanyDomain: {type: 'STRING',},
            Product: {type: 'STRING',},
            Vendor: {type: 'STRING',},
            VendorDomain: {type: 'STRING',},
            Country: {type: 'STRING',},
            Source: {type: 'STRING',},
            HitCount:{type:'NUMBER'},
            DateFirstVerified: {type: 'STRING',},
            DateLastVerified: {type: 'STRING',},
          },
        },
      },
    },
    required: ['products'],
    type: 'OBJECT',
  };

  private csvCompaniesResponseSchema = {
    type: 'OBJECT',
    properties: {
      companies: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            Company: {type: 'STRING'},
            CompanyDomain: {type: 'STRING'},
            Product: {type: 'STRING'},
            Vendor: {type: 'STRING'},
            VendorDomain: {type: 'STRING'},
            HitCount: {type: 'NUMBER'},
            Country: {type: 'STRING'},
            Scope: {type: 'STRING'},
            Source: {type: 'STRING'},
            Probability: {type: 'STRING'},
            DateFirstVerified: {type: 'STRING'},
            DateLastVerified: {type: 'STRING'},
          },
          propertyOrdering: [
            'Company',
            'CompanyDomain',
            'Product',
            'Vendor',
            'VendorDomain',
            'HitCount',
            'Country',
            'Scope',
            'Source',
            'Probability',
            'DateFirstVerified',
            'DateLastVerified',
          ],
          required: [
            'Company',
            'CompanyDomain',
            'Product',
            'Vendor',
            'VendorDomain',
            'HitCount',
            'Country',
            'Scope',
            'Source',
            'Probability',
            'DateFirstVerified',
            'DateLastVerified',
          ],
        },
      },
    },
    propertyOrdering: ['companies'],
    required: ['companies'],
  };


  constructor() {
    this.ai = new GoogleGenAI({
      apiKey: 'AIzaSyCEQ-DkCwkA3ksRpzaNsgB8m1eEfkLADhA'
    });
  }

  async analyzePrompt(
    systemInstruction: string,
    userPrompt: string,
    schemaType: GeminiSchemaType
  ): Promise<any> {
    const config = {
      responseMimeType: 'application/json',
      responseSchema: this.getSchemaByType(schemaType),
      systemInstruction: [{ text: systemInstruction }]
    };
    const contents = [
      {
        role: 'user',
        parts: [{ text: userPrompt }]
      }
    ];

    const model = this.ai.models;
    const response = await model.generateContent({
      model: this.modelName,
      config,
      contents
    });

    try {
      return response.text;
    } catch (err) {
      console.error('Error parsing response:', response.data);
      throw new Error('Failed to parse Gemini response');
    }
  }

  async detectIntent(
    userPrompt: string, 
    commands: string[],
    currentMode: string,
    recentHistory: {role: string, text: string}[]
  ): Promise<string> {
    const config = {
      responseMimeType: 'application/json',
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          intent: {
            type: Type.STRING,
            enum: commands
          }
        },
        required: ['intent']
      },
      systemInstruction: [{ 
        text: 'You are an expert intent classifier for an AI assistant. Analyze the new user message in the context of the recent conversation and current mode. Classify it into the most appropriate category from the provided list. If the user is asking a follow-up question (like "give me more", "what about", "expand on this") related to the current task, you MUST return the current mode to maintain context. If the user asks about API, endpoints, tokens, authentication, MCP, or how to use the API/MCP, select the "API Documentation" mode. Return ONLY a JSON object with the "intent" field.' 
      }]
    };

    const historyText = recentHistory.map(m => `${m.role.toUpperCase()}: ${m.text}`).join('\n');
    const fullPrompt = `Current Mode: ${currentMode}\n\nRecent Conversation History (Truncated):\n${historyText || 'No history'}\n\nNew User Message: ${userPrompt}\n\nDetermine the intent.`;

    const model = this.ai.models;
    try {
      const response = await model.generateContent({
        model: this.modelName,
        config,
        contents: [{ role: 'user', parts: [{ text: fullPrompt }] }]
      });
      const result = JSON.parse(response.text);
      return result.intent;
    } catch (err) {
      console.error('Intent detection failed:', err);
      return '';
    }
  }

  private getSchemaByType(schemaType: GeminiSchemaType) {
    switch (schemaType) {
      case GeminiSchemaType.Profiles:
        return this.profilesResponseSchema;
      case GeminiSchemaType.Companies:
        return this.companiesResponseSchema;
      case GeminiSchemaType.Prompts:
        return this.promptsResponseSchema;
      case GeminiSchemaType.CSVCompanies:
        return this.csvCompaniesResponseSchema;
      case GeminiSchemaType.CSVProducts:
       return this.csvProductsResponseSchema;
      default:
        throw new Error(`Unsupported schema type: ${schemaType}`);
    }
  }


}

